import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Container, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, Tooltip,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControlLabel, Switch, Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Block as RevokeIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../utils/api';
import { useAuth } from '../store/authStore';
import { ConfirmDialog } from '../components/common';
import copyText from '../utils/copyText';

const STATUS_COLORS = { active: 'success', revoked: 'default', expired: 'warning' };

const formatDate = (value) => (value ? new Date(value).toLocaleString() : '—');

const ApiKeysPage = () => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const isSuperuser = !!user?.is_superuser;

  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [expiresDate, setExpiresDate] = useState('');
  const [creating, setCreating] = useState(false);
  // The full key lives only in this component state, never in storage or logs.
  const [createdKey, setCreatedKey] = useState(null);

  const [revokeTarget, setRevokeTarget] = useState(null);

  const fetchKeys = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/api-keys', {
        params: isSuperuser && showAll ? { all: true } : undefined,
      });
      setKeys(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      enqueueSnackbar('Failed to load API keys', { variant: 'error' });
      setKeys([]);
    } finally {
      setLoading(false);
    }
  }, [isSuperuser, showAll, enqueueSnackbar]);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const closeCreate = () => {
    setCreateOpen(false);
    setName('');
    setExpiresDate('');
  };

  const handleCreate = async () => {
    const payload = { name: name.trim() };
    if (expiresDate) {
      // Expire at the end of the chosen local day
      payload.expires_at = new Date(`${expiresDate}T23:59:59`).toISOString();
    }
    try {
      setCreating(true);
      const response = await api.post('/api-keys', payload);
      setCreatedKey(response.data);
      closeCreate();
      fetchKeys();
    } catch (error) {
      const detail = error.response?.data?.detail;
      enqueueSnackbar(
        typeof detail === 'string' ? detail : 'Failed to create API key',
        { variant: 'error' }
      );
    } finally {
      setCreating(false);
    }
  };

  const handleCopyKey = async () => {
    try {
      await copyText(createdKey.key);
      enqueueSnackbar('Copied to clipboard', { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to copy', { variant: 'error' });
    }
  };

  const handleRevoke = async () => {
    const target = revokeTarget;
    setRevokeTarget(null);
    try {
      await api.delete(`/api-keys/${target.id}`);
      enqueueSnackbar('API key revoked', { variant: 'success' });
      fetchKeys();
    } catch (error) {
      enqueueSnackbar('Failed to revoke API key', { variant: 'error' });
    }
  };

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}`;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          API keys
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {isSuperuser && (
            <FormControlLabel
              control={
                <Switch checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
              }
              label="All users"
            />
          )}
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            Create key
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 3 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : keys.length === 0 ? (
          <Box textAlign="center" p={4}>
            <Typography variant="h6" gutterBottom>No API keys</Typography>
            <Typography variant="body1" color="textSecondary">
              Create a key to call API endpoints from scripts and other systems.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  {showAll && <TableCell>Owner</TableCell>}
                  <TableCell>Key</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Last used</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {keys.map((k) => (
                  <TableRow key={k.id}>
                    <TableCell>{k.name}</TableCell>
                    {showAll && <TableCell>{k.owner_username || '—'}</TableCell>}
                    <TableCell><code>{k.prefix}…</code></TableCell>
                    <TableCell>{formatDate(k.created_at)}</TableCell>
                    <TableCell>{k.last_used_at ? formatDate(k.last_used_at) : 'Never'}</TableCell>
                    <TableCell>{k.expires_at ? formatDate(k.expires_at) : 'Never'}</TableCell>
                    <TableCell>
                      <Chip
                        label={k.status}
                        size="small"
                        color={STATUS_COLORS[k.status] || 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {k.status === 'active' && (
                        <Tooltip title="Revoke">
                          <IconButton
                            size="small"
                            color="error"
                            aria-label={`Revoke ${k.name}`}
                            onClick={() => setRevokeTarget(k)}
                          >
                            <RevokeIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={createOpen} onClose={closeCreate} maxWidth="xs" fullWidth>
        <DialogTitle>Create API key</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            required
            fullWidth
            margin="dense"
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            inputProps={{ maxLength: 100 }}
            helperText="For example, the system that will use this key"
          />
          <TextField
            fullWidth
            margin="dense"
            type="date"
            label="Expires (optional)"
            value={expiresDate}
            onChange={(e) => setExpiresDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: minDate }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreate}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!name.trim() || creating}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!createdKey} maxWidth="sm" fullWidth disableEscapeKeyDown>
        <DialogTitle>API key created</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Copy this key now. You won't be able to see it again.
          </Alert>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              component="code"
              data-testid="new-api-key"
              sx={{
                flex: 1,
                p: 1.5,
                fontFamily: 'monospace',
                wordBreak: 'break-all',
                bgcolor: 'action.hover',
                borderRadius: 1,
              }}
            >
              {createdKey?.key}
            </Box>
            <Tooltip title="Copy key">
              <IconButton aria-label="Copy key" onClick={handleCopyKey}>
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setCreatedKey(null)}>Done</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!revokeTarget}
        title="Revoke API key"
        message={`Revoke "${revokeTarget?.name}"? Anything using this key will stop working immediately.`}
        confirmLabel="Revoke"
        confirmColor="error"
        onConfirm={handleRevoke}
        onCancel={() => setRevokeTarget(null)}
      />
    </Container>
  );
};

export default ApiKeysPage;
